import { z } from 'zod';
import {
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
  REMINDER_OPTIONS,
  VISIBILITIES,
  VISIT_STATUSES,
} from '../constants';
import { MAX_AMOUNT_GROSZE } from '../money';
import { normalizePhone } from '../phone';

export const MIN_DURATION_MINUTES = 5;
export const MAX_DURATION_MINUTES = 720;
export const MIN_ARRIVAL_WINDOW_MINUTES = 15;
export const MAX_ARRIVAL_WINDOW_MINUTES = 720;

const optionalText = (max: number, message: string) =>
  z
    .string()
    .trim()
    .max(max, message)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const id = z.number().int().positive();
const unique = (ids: number[]) => new Set(ids).size === ids.length;

/** Per-visit reminder override: null = the assignee's default, 0 = no reminder. */
export const visitReminderSchema = z
  .number()
  .int()
  .refine((v) => v === 0 || (REMINDER_OPTIONS as readonly number[]).includes(v), 'Wybierz jedną z dostępnych opcji.')
  .nullable();

const visitFields = {
  /** ISO instant with offset, e.g. 2026-10-21T10:00:00.000+02:00. */
  startAt: z.iso.datetime({ offset: true, error: 'Wybierz datę i godzinę.' }),
  durationMinutes: z
    .number({ error: 'Wybierz czas trwania.' })
    .int('Czas trwania podaj w pełnych minutach.')
    .min(MIN_DURATION_MINUTES, `Czas trwania musi wynosić co najmniej ${MIN_DURATION_MINUTES} minut.`)
    .max(MAX_DURATION_MINUTES, 'Czas trwania może wynosić najwyżej 12 godzin.'),
  clientId: id.nullable().optional(),
  objectId: id.nullable().optional(),
  address: optionalText(255, 'Adres może mieć najwyżej 255 znaków.'),
  lat: z.number().min(-90).max(90).nullable().optional(),
  lng: z.number().min(-180).max(180).nullable().optional(),
  phone: optionalText(32, 'Numer telefonu jest za długi.').refine(
    (v) => v == null || normalizePhone(v) !== null,
    'Wpisz poprawny numer telefonu, np. 600 123 456.',
  ),
  /** Every service on the visit, in the order picked (the first is the main one). */
  serviceTypeIds: z.array(id).max(10, 'Można wybrać najwyżej 10 usług.').refine(unique, 'Każdą usługę wybierz tylko raz.'),
  description: optionalText(2000, 'Opis może mieć najwyżej 2000 znaków.'),
  amountGrosze: z
    .number()
    .int()
    .min(0, 'Kwota nie może być ujemna.')
    .max(MAX_AMOUNT_GROSZE, 'Kwota jest za duża.')
    .nullable()
    .optional(),
  /** Everyone who goes, in the order picked; the first one leads (reminders, colour). */
  assigneeIds: z
    .array(id, { error: 'Wybierz wykonawcę.' })
    .min(1, 'Wybierz wykonawcę.')
    .max(10, 'Można wybrać najwyżej 10 osób.')
    .refine(unique, 'Każdą osobę wybierz tylko raz.'),
  /** "Arrive between start and start + window"; null = an exact time. */
  arrivalWindowMinutes: z
    .number()
    .int()
    .min(MIN_ARRIVAL_WINDOW_MINUTES, `Przedział przyjazdu musi trwać co najmniej ${MIN_ARRIVAL_WINDOW_MINUTES} minut.`)
    .max(MAX_ARRIVAL_WINDOW_MINUTES, 'Przedział przyjazdu może trwać najwyżej 12 godzin.')
    .nullable()
    .optional(),
  visibility: z.enum(VISIBILITIES),
  sharedWith: z.array(id).max(50),
  reminderMinutes: visitReminderSchema.optional(),
};

export const visitCreateSchema = z
  .object({
    ...visitFields,
    visibility: visitFields.visibility.default('private'),
    sharedWith: visitFields.sharedWith.default([]),
    serviceTypeIds: visitFields.serviceTypeIds.default([]),
  })
  .refine((d) => Boolean(d.address) || Boolean(d.clientId), {
    path: ['address'],
    message: 'Wpisz adres albo wybierz klienta.',
  });
export type VisitCreateRequest = z.input<typeof visitCreateSchema>;

/** Core data; the address-or-client rule is checked against the merged result. */
export const visitUpdateSchema = z.object(visitFields).partial();
export type VisitUpdateRequest = z.input<typeof visitUpdateSchema>;

export const localDateSchema = z.iso.date({ error: 'Wybierz datę.' });

/** What the assignee may change: status, note after the visit, payment. */
export const visitClosingSchema = z.object({
  status: z.enum(VISIT_STATUSES).optional(),
  afterNote: optionalText(4000, 'Notatka może mieć najwyżej 4000 znaków.'),
  paymentStatus: z.enum(PAYMENT_STATUSES).optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).nullable().optional(),
  paidAt: localDateSchema.nullable().optional(),
  /** Next periodic inspection for the visit's address (client object); null clears it. */
  nextInspectionDue: localDateSchema.nullable().optional(),
});
export type VisitClosingRequest = z.input<typeof visitClosingSchema>;

/** "team": visits with more than one person. */
export const VISIT_FILTER_PATTERN = /^(mine|all|team|user:\d+)$/;

export const visitListQuerySchema = z.object({
  from: z.iso.datetime({ offset: true }),
  to: z.iso.datetime({ offset: true }),
  filter: z.string().regex(VISIT_FILTER_PATTERN).default('all'),
});

export const overlapQuerySchema = z.object({
  /** Comma-separated user ids. */
  assigneeIds: z
    .string()
    .regex(/^\d+(,\d+){0,9}$/)
    .transform((v) => v.split(',').map(Number)),
  startAt: z.iso.datetime({ offset: true }),
  durationMinutes: z.coerce.number().int().min(MIN_DURATION_MINUTES).max(MAX_DURATION_MINUTES),
  arrivalWindowMinutes: z.coerce.number().int().min(MIN_ARRIVAL_WINDOW_MINUTES).max(MAX_ARRIVAL_WINDOW_MINUTES).optional(),
  excludeId: z.coerce.number().int().positive().optional(),
});

export const serviceTypeCreateSchema = z.object({
  name: z.string().trim().min(2, 'Wpisz nazwę usługi.').max(100, 'Nazwa może mieć najwyżej 100 znaków.'),
  isPeriodic: z.boolean().default(false),
  periodMonths: z
    .number()
    .int()
    .min(1, 'Okres musi wynosić co najmniej 1 miesiąc.')
    .max(60, 'Okres może wynosić najwyżej 60 miesięcy.')
    .default(12),
});
export type ServiceTypeCreateRequest = z.input<typeof serviceTypeCreateSchema>;

export const serviceTypeUpdateSchema = z
  .object({
    name: serviceTypeCreateSchema.shape.name,
    isPeriodic: z.boolean(),
    periodMonths: z.number().int().min(1).max(60),
    isActive: z.boolean(),
    sortOrder: z.number().int().min(0).max(10_000),
  })
  .partial();
export type ServiceTypeUpdateRequest = z.input<typeof serviceTypeUpdateSchema>;

/** All service type ids in the new display order. */
export const serviceTypeOrderSchema = z.object({ ids: z.array(id).min(1).max(500) });

export const monthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Wybierz miesiąc.');
export const monthlyReportQuerySchema = z.object({ month: monthSchema });
export const monthlyCsvQuerySchema = z.object({ month: monthSchema, typ: z.enum(['wizyty', 'podsumowanie']).default('wizyty') });
export const dueQuerySchema = z.object({ days: z.coerce.number().int().min(0).max(366).default(30) });
